<script setup lang="ts">
import { computed, type Component, useAttrs } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

defineOptions({ inheritAttrs: false, name: 'EGS_Button' });

type IconValue = string | [string, string];

interface EgsButtonProps {
	component?: string | Component;
	type?: 'button' | 'submit' | 'reset';
	to?: string | Record<string, unknown> | null;
	href?: string | null;
	label?: string;
	icon?: IconValue | null;
	iconPosition?: 'left' | 'right' | 'top' | 'bottom';
	iconPos?: 'left' | 'right' | 'top' | 'bottom';
	variant?: string;
	severity?: string;
	size?: string;
	disabled?: boolean;
	loading?: boolean;
	outlined?: boolean;
	text?: boolean;
	rounded?: boolean;
	raised?: boolean;
	fluid?: boolean;
	badge?: string | number | null;
	ariaLabel?: string;
	title?: string;
}

const props = withDefaults(defineProps<EgsButtonProps>(), {
	component: 'button',
	type: 'button',
	to: null,
	href: null,
	label: '',
	icon: null,
	iconPosition: 'left',
	variant: '',
	severity: 'primary',
	size: 'md',
	disabled: false,
	loading: false,
	outlined: false,
	text: false,
	rounded: true,
	raised: false,
	fluid: false,
	badge: null,
	ariaLabel: '',
	title: ''
});

const emit = defineEmits<{ click: [event: MouseEvent] }>();
const attrs = useAttrs();

const severityClasses: Record<string, { solid: string; outline: string; text: string }> = {
	primary: {
		solid: 'border-emerald-600 bg-emerald-600 text-white hover:border-emerald-700 hover:bg-emerald-700 active:bg-emerald-800',
		outline: 'border-emerald-600 bg-transparent text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/40',
		text: 'border-transparent bg-transparent text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/40'
	},
	secondary: {
		solid: 'border-gray-600 bg-gray-600 text-white hover:border-gray-700 hover:bg-gray-700 active:bg-gray-800',
		outline: 'border-surface bg-transparent text-color-secondary hover:bg-[var(--egs-content-hover)] hover:text-color',
		text: 'border-transparent bg-transparent text-color-secondary hover:bg-[var(--egs-content-hover)] hover:text-color'
	},
	success: {
		solid: 'border-emerald-600 bg-emerald-600 text-white hover:border-emerald-700 hover:bg-emerald-700 active:bg-emerald-800',
		outline: 'border-emerald-600 bg-transparent text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/40',
		text: 'border-transparent bg-transparent text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/40'
	},
	info: {
		solid: 'border-sky-600 bg-sky-600 text-white hover:border-sky-700 hover:bg-sky-700 active:bg-sky-800',
		outline: 'border-sky-600 bg-transparent text-sky-700 hover:bg-sky-50 dark:text-sky-300 dark:hover:bg-sky-950/40',
		text: 'border-transparent bg-transparent text-sky-700 hover:bg-sky-50 dark:text-sky-300 dark:hover:bg-sky-950/40'
	},
	warn: {
		solid: 'border-amber-500 bg-amber-500 text-amber-950 hover:border-amber-600 hover:bg-amber-600 active:bg-amber-700',
		outline: 'border-amber-500 bg-transparent text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-950/40',
		text: 'border-transparent bg-transparent text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-950/40'
	},
	danger: {
		solid: 'border-red-600 bg-red-600 text-white hover:border-red-700 hover:bg-red-700 active:bg-red-800',
		outline: 'border-red-600 bg-transparent text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/40',
		text: 'border-transparent bg-transparent text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/40'
	},
	contrast: {
		solid: 'border-slate-950 bg-slate-950 text-white hover:bg-slate-800 dark:border-white dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200',
		outline: 'border-slate-800 bg-transparent text-slate-900 hover:bg-slate-100 dark:border-slate-200 dark:text-white dark:hover:bg-slate-800',
		text: 'border-transparent bg-transparent text-color hover:bg-[var(--egs-content-hover)]'
	}
};

const normalizedSeverity = computed(() => {
	const requested = props.variant && !['outline', 'ghost'].includes(props.variant) ? props.variant : props.severity;
	const value = requested === 'warning' ? 'warn' : requested;
	return value in severityClasses ? value : 'primary';
});
const visualMode = computed<'solid' | 'outline' | 'text'>(() => (props.text || props.variant === 'ghost' ? 'text' : props.outlined || props.variant === 'outline' ? 'outline' : 'solid'));
const resolvedIconPosition = computed(() => props.iconPos ?? props.iconPosition);
const iconFirst = computed(() => resolvedIconPosition.value === 'left' || resolvedIconPosition.value === 'top');
const iconValue = computed<IconValue | null>(() => (props.icon ? (Array.isArray(props.icon) ? props.icon : ['fas', props.icon]) : null));
const isDisabled = computed(() => props.disabled || props.loading);
const sizeClass = computed(() => {
	if (props.size === 'small' || props.size === 'sm') return 'px-3 py-1.5 text-xs';
	if (props.size === 'large' || props.size === 'lg') return 'px-6 py-3 text-base';
	return 'px-5 py-2 text-sm';
});
const buttonClasses = computed(() => {
	const severity = severityClasses[normalizedSeverity.value] ?? severityClasses['primary']!;
	return [
		'egs-button inline-flex select-none items-center justify-center gap-2 border font-semibold whitespace-nowrap transition',
		'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950',
		props.rounded ? 'rounded-full' : 'rounded-lg',
		resolvedIconPosition.value === 'top' || resolvedIconPosition.value === 'bottom' ? 'flex-col' : '',
		sizeClass.value,
		props.fluid ? 'w-full' : '',
		props.raised ? 'shadow-md hover:shadow-lg' : '',
		severity[visualMode.value],
		isDisabled.value ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
	];
});

function onClick(event: MouseEvent) {
	if (!isDisabled.value) emit('click', event);
}
</script>

<template>
	<button
		v-if="component === 'button'"
		v-bind="attrs"
		:class="[buttonClasses, attrs['class']]"
		:type="type"
		:title="title"
		:disabled="isDisabled"
		:aria-disabled="isDisabled ? 'true' : 'false'"
		:aria-label="ariaLabel || label"
		:aria-busy="loading ? 'true' : 'false'"
		@click="onClick"
	>
		<FontAwesomeIcon v-if="loading" :icon="['fas', 'spinner']" spin />
		<slot v-else-if="iconFirst" name="icon"><FontAwesomeIcon v-if="iconValue" :icon="iconValue" /></slot>
		<span v-if="label">{{ label }}</span>
		<slot v-else />
		<slot v-if="!loading && !iconFirst" name="icon"><FontAwesomeIcon v-if="iconValue" :icon="iconValue" /></slot>
		<span v-if="badge != null" class="rounded-full bg-black/15 px-1.5 py-0.5 text-[0.65rem] leading-none">{{ badge }}</span>
	</button>
	<component
		:is="component"
		v-else
		v-bind="attrs"
		:class="[buttonClasses, attrs['class']]"
		:to="to || undefined"
		:href="href || undefined"
		:title="title || undefined"
		:aria-disabled="isDisabled ? 'true' : undefined"
		:aria-label="ariaLabel || label || undefined"
		:aria-busy="loading ? 'true' : undefined"
		@click="onClick"
	>
		<FontAwesomeIcon v-if="loading" :icon="['fas', 'spinner']" spin />
		<slot v-else-if="iconFirst" name="icon"><FontAwesomeIcon v-if="iconValue" :icon="iconValue" /></slot>
		<span v-if="label">{{ label }}</span>
		<slot v-else />
		<slot v-if="!loading && !iconFirst" name="icon"><FontAwesomeIcon v-if="iconValue" :icon="iconValue" /></slot>
		<span v-if="badge != null" class="rounded-full bg-black/15 px-1.5 py-0.5 text-[0.65rem] leading-none">{{ badge }}</span>
	</component>
</template>
